// Rooms CRUD operations using direct fetch (bypassing Supabase client)

/// <reference lib="dom" />

import { handleApiCall } from './errors'
import { getSchoolContext } from './school-context'
import { getAccessToken } from './auth-token'
import type { Room, CreateRoomRequest, UpdateRoomRequest, RoomFilters } from '@timetable/types'

// Get environment variables
const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}

// Get all rooms with filters
export async function getRoomsDirect(filters: RoomFilters = {}): Promise<Room[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*')

    const schoolContext = getSchoolContext()
    const schoolId = filters.school_id || schoolContext

    if (schoolId) {
      params.append('school_id', `eq.${schoolId}`)
    }
    if (filters.search) {
      params.append('name', `ilike.*${filters.search}*`)
    }
    params.append('order', 'name.asc')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/rooms?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch rooms')
    }

    return await response.json()
  })
}

// Get a single room by ID
export async function getRoomByIdDirect(id: string): Promise<Room> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*')
    params.append('id', `eq.${id}`)

    const response = await fetch(`${SUPABASE_URL}/rest/v1/rooms?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/vnd.pgrst.object+json',
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Room not found')
    }

    return await response.json()
  })
}

// Create a new room
export async function createRoomDirect(req: CreateRoomRequest): Promise<Room> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/rooms`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(req),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to create room')
    }

    const data: Room[] = await response.json()
    return data[0]
  })
}

// Update a room
export async function updateRoomDirect(id: string, req: UpdateRoomRequest): Promise<Room> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/rooms?id=eq.${id}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(req),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to update room')
    }

    const data: Room[] = await response.json()
    if (!data[0]) throw new Error('Room not found')

    return data[0]
  })
}

// Delete a room
export async function deleteRoomDirect(id: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/rooms?id=eq.${id}`, {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to delete room')
    }
  })
}
